import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Package,
  Plus,
  RefreshCw,
  Trash2,
  WifiOff,
} from 'lucide-react'
import api from '@/lib/api.ts'
import { useTenantStore } from '@/stores/tenants.ts'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
  type ColumnDef,
} from '@/components/ui'

// ─── Types ───────────────────────────────────────────────────────────────────

interface SapOrderLine {
  lineNum: number
  itemCode: string
  itemName: string
  quantity: number
  unitPrice: number
  isInventoryItem: boolean
  lineTotal: number
  quantityOnStock: number
  stockSufficient: boolean
}

interface SapOrderWithStock {
  docEntry: number
  docNum: number
  docDate: string
  cardCode: string
  cardName: string
  hasShortage: boolean
  lines: SapOrderLine[]
  tenantId: string
}

interface SapOrdersStockResponse {
  available: boolean
  orders: SapOrderWithStock[]
  message?: string
  warnings?: string[]
  dateFrom?: string
  daysBack?: number
}

type BedarfStatus = 'offen' | 'in_anfrage' | 'bestellt'

interface Bedarf {
  id: string
  tenantId: string
  itemCode: string | null
  itemName: string
  quantity: string
  unit: string | null
  status: BedarfStatus
  sourceSapDocEntry: number | null
  sourceSapDocNum: number | null
  sourceCardCode: string | null
  sourceCardName: string | null
  tanssTicketId: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
}

const STATUS_OPTIONS: { value: BedarfStatus; label: string }[] = [
  { value: 'offen', label: 'Offen' },
  { value: 'in_anfrage', label: 'In Anfrage' },
  { value: 'bestellt', label: 'Bestellt' },
]

// ─── Page ────────────────────────────────────────────────────────────────────

export function BedarfserfassungPage() {
  const [activeTab, setActiveTab] = useState<'auftraege' | 'bedarfe'>('auftraege')
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set())
  const [showOnlyShortage, setShowOnlyShortage] = useState(false)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [addFromLine, setAddFromLine] = useState<{ order: SapOrderWithStock; line: SapOrderLine } | null>(null)
  const { toast } = useToast()

  const activeTenantId = useTenantStore((s) => s.activeTenantId)
  const tenantId = activeTenantId ?? ''
  const allTenants = useTenantStore((s) => s.tenants)
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const tenantNames: Record<string, string> = useMemo(
    () => Object.fromEntries(allTenants.map((t) => [t.id, t.code?.toUpperCase() ?? t.name])),
    [allTenants],
  )

  const qc = useQueryClient()

  const ordersQuery = useQuery<SapOrdersStockResponse>({
    queryKey: ['einkauf-sap-orders-stock', activeTenantIds],
    queryFn: () => api.get('/einkauf/bedarfe/sap-orders-stock').then((r) => r.data),
    retry: false,
    staleTime: 2 * 60 * 1000,
  })

  const bedarfeQuery = useQuery<Bedarf[]>({
    queryKey: ['einkauf-bedarfe'],
    queryFn: () => api.get('/einkauf/bedarfe').then((r) => r.data),
    staleTime: 30 * 1000,
  })

  const filteredOrders = useMemo(() => {
    const orders = ordersQuery.data?.orders ?? []
    if (!showOnlyShortage) return orders
    return orders.filter((o) => o.hasShortage)
  }, [ordersQuery.data, showOnlyShortage])

  function toggleOrder(docEntry: number) {
    setExpandedOrders((prev) => {
      const next = new Set(prev)
      if (next.has(docEntry)) next.delete(docEntry)
      else next.add(docEntry)
      return next
    })
  }

  function openAddFromSap(order: SapOrderWithStock, line: SapOrderLine) {
    setAddFromLine({ order, line })
    setAddModalOpen(true)
  }

  function openManualAdd() {
    setAddFromLine(null)
    setAddModalOpen(true)
  }

  const shortageCount = (ordersQuery.data?.orders ?? []).filter((o) => o.hasShortage).length
  const offeneBedarfe = (bedarfeQuery.data ?? []).filter((b) => b.status === 'offen').length

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bedarfserfassung"
        description="Offene SAP-Aufträge gegen Lagerbestand prüfen und fehlende Artikel als Bedarfe erfassen"
        breadcrumbs={[{ label: 'Beschaffung' }, { label: 'Bedarfserfassung' }]}
        actions={
          <Button variant="primary" size="sm" onClick={openManualAdd}>
            <Plus className="h-4 w-4 mr-1" />
            Bedarf manuell erfassen
          </Button>
        }
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="auftraege">
            SAP-Aufträge
            {shortageCount > 0 && (
              <Badge variant="danger" size="sm" className="ml-2">
                {shortageCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="bedarfe">
            Bedarfsliste
            {offeneBedarfe > 0 && (
              <Badge variant="warning" size="sm" className="ml-2">
                {offeneBedarfe}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="auftraege" className="mt-4">
          <SapAuftraegeTab
            query={ordersQuery}
            filteredOrders={filteredOrders}
            showOnlyShortage={showOnlyShortage}
            expandedOrders={expandedOrders}
            tenantNames={tenantNames}
            onToggleShortage={() => setShowOnlyShortage((p) => !p)}
            onToggleOrder={toggleOrder}
            onAddBedarf={openAddFromSap}
          />
        </TabsContent>

        <TabsContent value="bedarfe" className="mt-4">
          <BedarfeTab
            query={bedarfeQuery}
            onRefresh={() => qc.invalidateQueries({ queryKey: ['einkauf-bedarfe'] })}
            toast={toast}
          />
        </TabsContent>
      </Tabs>

      {addModalOpen && (
        <AddBedarfModal
          fromSap={addFromLine}
          tenantId={tenantId}
          onClose={() => { setAddModalOpen(false); setAddFromLine(null) }}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['einkauf-bedarfe'] })
            setAddModalOpen(false)
            setAddFromLine(null)
            toast({ title: 'Bedarf erfasst', tone: 'success' })
          }}
        />
      )}
    </div>
  )
}

// ─── SAP Orders Tab ──────────────────────────────────────────────────────────

function SapAuftraegeTab({
  query,
  filteredOrders,
  showOnlyShortage,
  expandedOrders,
  tenantNames,
  onToggleShortage,
  onToggleOrder,
  onAddBedarf,
}: {
  query: ReturnType<typeof useQuery<SapOrdersStockResponse>>
  filteredOrders: SapOrderWithStock[]
  showOnlyShortage: boolean
  expandedOrders: Set<number>
  tenantNames: Record<string, string>
  onToggleShortage: () => void
  onToggleOrder: (docEntry: number) => void
  onAddBedarf: (order: SapOrderWithStock, line: SapOrderLine) => void
}) {
  if (query.isLoading) return <LoadingState label="SAP-Aufträge werden geladen und Lagerbestand geprüft..." />

  if (query.isError) {
    return (
      <ErrorState
        title="SAP-Aufträge konnten nicht geladen werden"
        description={
          (query.error as { response?: { data?: { error?: string; message?: string } } })
            ?.response?.data?.error ??
          (query.error as { response?: { data?: { error?: string; message?: string } } })
            ?.response?.data?.message ??
          (query.error instanceof Error ? query.error.message : 'Unbekannter Fehler')
        }
        onRetry={() => query.refetch()}
      />
    )
  }

  if (!query.data?.available) {
    return (
      <Card>
        <Card.Body>
          <div className="flex items-center gap-3">
            <WifiOff className="h-6 w-6 text-gray-400 shrink-0" />
            <div>
              <div className="font-medium text-gray-900">SAP nicht verfügbar</div>
              <div className="text-small text-gray-500">
                {query.data?.message ?? 'SAP-Verbindung nicht konfiguriert. Bedarfe können weiterhin manuell erfasst werden.'}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    )
  }

  const warnings = query.data?.warnings
  const dateFrom = query.data?.dateFrom
  const totalOrders = query.data?.orders?.length ?? 0

  return (
    <div className="space-y-3">
      {warnings && warnings.length > 0 && (
        <Card>
          <Card.Body>
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-amber-800 text-small">SAP-Fehler bei {warnings.length > 1 ? `${warnings.length} Mandanten` : 'einem Mandanten'}</div>
                {warnings.map((w, i) => (
                  <div key={i} className="text-caption text-amber-700 mt-1 break-all">{w}</div>
                ))}
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-small text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyShortage}
              onChange={onToggleShortage}
              className="h-4 w-4"
            />
            Nur Aufträge mit Lieferengpässen anzeigen
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="ml-auto"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${query.isFetching ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </Card.Body>
      </Card>

      {filteredOrders.length === 0 ? (
        <EmptyState
          title={showOnlyShortage ? 'Keine Lieferengpässe' : 'Keine offenen Aufträge'}
          description={
            showOnlyShortage
              ? `Alle Artikel in den ${totalOrders} geladenen offenen Aufträgen sind auf Lager oder nicht lagerpflichtig.`
              : dateFrom
                ? `Es wurden ${totalOrders} offene SAP-Aufträge ab ${new Date(dateFrom).toLocaleDateString('de-DE')} geprüft.${warnings?.length ? ' SAP-Fehler bei einigen Mandanten (siehe Warnung oben).' : ''}`
                : 'Es gibt keine offenen SAP-Aufträge der letzten 90 Tage.'
          }
          icon={<CheckCircle2 className="h-8 w-8 text-green-500" />}
        />
      ) : (
        <Card>
          <ul className="divide-y divide-gray-200">
            {filteredOrders.map((order) => {
              const isOpen = expandedOrders.has(order.docEntry)
              const shortageLines = order.lines.filter((l) => !l.stockSufficient)
              return (
                <li key={order.docEntry}>
                  <button
                    type="button"
                    onClick={() => onToggleOrder(order.docEntry)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">#{order.docNum}</span>
                        <span className="text-small text-gray-500 truncate">{order.cardName}</span>
                        {tenantNames[order.tenantId] && (
                          <Badge variant="neutral" size="sm">{tenantNames[order.tenantId]}</Badge>
                        )}
                      </div>
                      <div className="text-caption text-gray-400">
                        {new Date(order.docDate).toLocaleDateString('de-DE')} · {order.lines.length} Position{order.lines.length !== 1 ? 'en' : ''}
                      </div>
                    </div>
                    <div className="shrink-0">
                      {shortageLines.length > 0 ? (
                        <Badge variant="danger" size="sm">
                          <AlertTriangle className="h-3 w-3 mr-1 inline" />
                          {shortageLines.length} Engpass{shortageLines.length !== 1 ? '' : ''}
                        </Badge>
                      ) : (
                        <Badge variant="success" size="sm">
                          <CheckCircle2 className="h-3 w-3 mr-1 inline" />
                          Lager OK
                        </Badge>
                      )}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="bg-gray-50 border-t border-gray-100 overflow-x-auto">
                      <table className="w-full text-small">
                        <thead>
                          <tr className="text-caption text-gray-500 border-b border-gray-200">
                            <th className="text-left px-4 py-2">Artikel</th>
                            <th className="text-right px-4 py-2">Bestellt</th>
                            <th className="text-right px-4 py-2">Lagerbestand</th>
                            <th className="text-right px-4 py-2">Status</th>
                            <th className="px-4 py-2 w-28" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {order.lines.map((line) => {
                            const isExcluded = !line.isInventoryItem
                            return (
                              <tr
                                key={line.lineNum}
                                className={
                                  isExcluded
                                    ? 'opacity-50'
                                    : !line.stockSufficient
                                      ? 'bg-red-50'
                                      : ''
                                }
                              >
                                <td className="px-4 py-2">
                                  <div className="font-medium text-gray-900 max-w-xs truncate">{line.itemName}</div>
                                  <div className="text-caption text-gray-400">{line.itemCode}</div>
                                </td>
                                <td className="px-4 py-2 text-right text-gray-700 tabular-nums">
                                  {line.quantity.toLocaleString('de-DE')}
                                </td>
                                <td className="px-4 py-2 text-right tabular-nums">
                                  {isExcluded ? (
                                    <span className="text-gray-400">–</span>
                                  ) : (
                                    <span className={line.stockSufficient ? 'text-green-700' : 'text-red-700 font-semibold'}>
                                      {line.quantityOnStock.toLocaleString('de-DE')}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  {isExcluded ? (
                                    <Badge variant="neutral" size="sm">Kein Lagerartikel</Badge>
                                  ) : line.stockSufficient ? (
                                    <Badge variant="success" size="sm">Auf Lager</Badge>
                                  ) : (
                                    <Badge variant="danger" size="sm">
                                      Fehlend: {Math.max(0, line.quantity - line.quantityOnStock).toLocaleString('de-DE')}
                                    </Badge>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  {!isExcluded && !line.stockSufficient && (
                                    <Button
                                      variant="ghost"
                                      size="xs"
                                      onClick={(e) => { e.stopPropagation(); onAddBedarf(order, line) }}
                                    >
                                      <Plus className="h-3 w-3 mr-1" />
                                      Als Bedarf
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </Card>
      )}
    </div>
  )
}

// ─── Bedarfe Tab ─────────────────────────────────────────────────────────────

function BedarfeTab({
  query,
  onRefresh,
  toast,
}: {
  query: ReturnType<typeof useQuery<Bedarf[]>>
  onRefresh: () => void
  toast: ReturnType<typeof useToast>['toast']
}) {
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const qc = useQueryClient()

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: BedarfStatus }) =>
      api.patch(`/einkauf/bedarfe/${id}`, { status }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['einkauf-bedarfe'] }),
    onError: () => toast({ title: 'Status konnte nicht aktualisiert werden', tone: 'danger' }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/einkauf/bedarfe/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['einkauf-bedarfe'] })
      toast({ title: 'Bedarf gelöscht', tone: 'success' })
      setDeleteId(null)
    },
    onError: () => toast({ title: 'Löschen fehlgeschlagen', tone: 'danger' }),
  })

  const columns = useMemo<ColumnDef<Bedarf, unknown>[]>(
    () => [
      {
        accessorKey: 'itemName',
        header: 'Artikel',
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-gray-900">{row.original.itemName}</div>
            {row.original.itemCode && (
              <div className="text-caption text-gray-400">{row.original.itemCode}</div>
            )}
            {row.original.sourceSapDocNum != null && (
              <div className="text-caption text-gray-400">
                SAP #{row.original.sourceSapDocNum}
                {row.original.sourceCardName ? ` · ${row.original.sourceCardName}` : ''}
              </div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'quantity',
        header: 'Menge',
        size: 100,
        cell: ({ row }) =>
          `${Number(row.original.quantity).toLocaleString('de-DE')} ${row.original.unit ?? 'Stk'}`,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 170,
        cell: ({ row }) => (
          <Select<BedarfStatus>
            value={row.original.status}
            onValueChange={(v) => updateMutation.mutate({ id: row.original.id, status: v })}
            options={STATUS_OPTIONS}
          />
        ),
      },
      {
        accessorKey: 'tanssTicketId',
        header: 'TANSS-Ticket',
        size: 130,
        cell: ({ row }) => row.original.tanssTicketId ?? <span className="text-gray-400">–</span>,
      },
      {
        accessorKey: 'createdAt',
        header: 'Erfasst',
        size: 120,
        cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString('de-DE'),
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setDeleteId(row.original.id)}
          >
            <Trash2 className="h-4 w-4 text-red-400 hover:text-red-600" />
          </Button>
        ),
      },
    ],
    [updateMutation],
  )

  if (query.isLoading) return <LoadingState label="Bedarfe werden geladen..." />

  if (query.isError) {
    return (
      <ErrorState
        title="Bedarfe konnten nicht geladen werden"
        description={query.error instanceof Error ? query.error.message : 'Unbekannter Fehler'}
        onRetry={onRefresh}
      />
    )
  }

  const bedarfe = query.data ?? []

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={query.isFetching}>
          <RefreshCw className={`h-4 w-4 mr-1 ${query.isFetching ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {bedarfe.length === 0 ? (
        <EmptyState
          title="Keine Bedarfe erfasst"
          description="Noch keine offenen Bedarfe. SAP-Aufträge prüfen oder manuell erfassen."
          icon={<Package className="h-8 w-8 text-gray-300" />}
        />
      ) : (
        <DataTable data={bedarfe} columns={columns} />
      )}

      <ConfirmDialog
        open={deleteId != null}
        title="Bedarf löschen"
        message="Soll dieser Bedarf unwiderruflich gelöscht werden?"
        confirmLabel="Löschen"
        variant="danger"
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        onCancel={() => setDeleteId(null)}
        loading={deleteMutation.isPending}
      />
    </div>
  )
}

// ─── Modal: Bedarf erfassen ───────────────────────────────────────────────────

function AddBedarfModal({
  fromSap,
  tenantId,
  onClose,
  onCreated,
}: {
  fromSap: { order: SapOrderWithStock; line: SapOrderLine } | null
  tenantId: string
  onClose: () => void
  onCreated: () => void
}) {
  const shortfall = fromSap
    ? Math.max(0, fromSap.line.quantity - fromSap.line.quantityOnStock)
    : 0

  const [itemName, setItemName] = useState(fromSap?.line.itemName ?? '')
  const [itemCode, setItemCode] = useState(fromSap?.line.itemCode ?? '')
  const [quantity, setQuantity] = useState(String(shortfall || fromSap?.line.quantity || 1))
  const [unit, setUnit] = useState('Stk')
  const [tanssTicketId, setTanssTicketId] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!tenantId) { setError('Kein aktiver Mandant ausgewählt'); return }
    if (!itemName.trim()) { setError('Artikelname ist erforderlich'); return }
    const qty = parseFloat(quantity)
    if (isNaN(qty) || qty <= 0) { setError('Menge muss größer als 0 sein'); return }
    setLoading(true)
    setError(null)
    try {
      await api.post('/einkauf/bedarfe', {
        tenantId,
        itemCode: itemCode.trim() || undefined,
        itemName: itemName.trim(),
        quantity: qty,
        unit: unit.trim() || 'Stk',
        sourceSapDocEntry: fromSap?.order.docEntry,
        sourceSapDocNum: fromSap?.order.docNum,
        sourceCardCode: fromSap?.order.cardCode,
        sourceCardName: fromSap?.order.cardName,
        tanssTicketId: tanssTicketId.trim() || undefined,
        notes: notes.trim() || undefined,
      })
      onCreated()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(msg ?? 'Bedarf konnte nicht gespeichert werden')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={fromSap ? 'Bedarf aus SAP-Auftrag' : 'Bedarf manuell erfassen'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {fromSap && (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-small text-amber-800">
            <div className="font-medium">SAP-Auftrag #{fromSap.order.docNum} · {fromSap.order.cardName}</div>
            <div className="mt-1">
              Lagerbestand: <strong>{fromSap.line.quantityOnStock}</strong> · Bestellt:{' '}
              <strong>{fromSap.line.quantity}</strong> · Fehlend: <strong>{shortfall}</strong>
            </div>
          </div>
        )}

        <Input
          label="Artikelname"
          value={itemName}
          onChange={(e) => setItemName(e.target.value)}
          required
          placeholder="z.B. Dell PowerEdge R650"
        />

        <Input
          label="Artikelnummer (SAP ItemCode)"
          value={itemCode}
          onChange={(e) => setItemCode(e.target.value)}
          placeholder="optional"
        />

        <div className="flex gap-3">
          <Input
            label="Menge"
            type="number"
            min="0.01"
            step="0.01"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="flex-1"
          />
          <Input
            label="Einheit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="Stk"
            className="w-24"
          />
        </div>

        <Input
          label="TANSS-Ticket-ID (optional)"
          value={tanssTicketId}
          onChange={(e) => setTanssTicketId(e.target.value)}
          placeholder="z.B. 12345"
        />

        <Textarea
          label="Anmerkungen (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Dringlich, Lieferant XY, ..."
        />

        {error && (
          <div className="text-small text-red-600 flex items-center gap-1">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Speichern...' : 'Bedarf erfassen'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
